import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { Box, Text, Space, Stack } from '@tonic-ui/react';

import { useTranslation, Trans } from 'react-i18next';
import { useRegionsList } from '../../hooks/useRegionsList';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import {
  DropdownMenu,
  FormContainer,
  InputField,
  accountSetupValidation,
  SubmitButton,
} from '../../components/forms';
import filterMessage from '../../utils/message-filter';
import { supportedLocales } from '../../locales/supportedLocales';
import { useMarketplaceDetails } from '../../hooks/useMarketplaceDetails';
import { waitFor, isGovCloudRegion } from '../../utils/helpers';
import { getCommonType } from '../../store';
import { shallowEqual, useSelector } from 'react-redux';
import ExternalLink from '../../components/external-link/external-link';
import OverlayLoader from '../../components/loader/overlay-loader';
import CenteredLayout from '../../components/centered-layout';

interface IProps {
  idToken: string;
  accountAlias: string;
  region: string;
}

const AccountSetup = (): JSX.Element => {
  const { authType = '' } = useSelector(
    (state) => ({
      authType: getCommonType(state, 'authType'),
    }),
    shallowEqual
  );
  const { eventDistributor, getUserAttributes } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({
    error: '',
  });
  const [isSAMLUser] = useState(authType === 'saml');
  const [regionsList, setRegionsList] = useState([]);
  const [userTimezone, setUserTimezone] = useState('');
  const [missingTimezone, setMissingTimezone] = useState(false);
  const { marketplaceProvider = '', marketplaceToken = '' } = useMarketplaceDetails();
  const [hasMarketplaceToken] = useState(marketplaceToken !== '');
  const [hasCampaignReferrer] = useState(localStorage.getItem('C1C_CAMPAIGN_REFERRER') || false);
  const [hasSourceAccountRequest] = useState(
    localStorage.getItem('C1C_SourceAccountIntegrationRequest') || false
  );
  const [hasGithubRequest] = useState(localStorage.getItem('C1C_GithubRequest') || false);
  const shouldNotRedirectToOnboarding =
    marketplaceToken ||
    hasCampaignReferrer ||
    hasSourceAccountRequest ||
    hasGithubRequest ||
    isGovCloudRegion;

  const [selectedRegion, setSelectedRegion] = useState('trend-us-1');

  const { status, data: regions } = useRegionsList();
  const { t } = useTranslation();

  useEffect(() => {
    if (isSAMLUser) {
      return eventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: 'home' });
    }
  }, [eventDistributor, isSAMLUser]);

  useEffect(() => {
    if (status !== 'loading' && regions) {
      setRegionsList(regions);
      setIsLoading(false);
    } else if (status === 'loading') {
      setIsLoading(true);
    }
  }, [status, regions]);

  useEffect(() => {
    if (authType !== 'saml' && status !== 'loading') {
      (async () => {
        try {
          const { zoneinfo } = await getUserAttributes();
          if (zoneinfo) {
            setUserTimezone(zoneinfo);
          } else if (!missingTimezone && !zoneinfo) {
            setMissingTimezone(true);
          }
        } catch (e) {
          console.error('Error unable getting zoneinfo', e);
        }
      })();
    }
  }, [authType, getUserAttributes, status]);

  const handleSubmit = async ({ accountAlias, region, setSubmitting, resetForm }) => {
    eventDistributor.dispatch({ type: 'TOGGLE_GLOBAL_LOADING_INDICATOR', loading: true });
    setSubmitting(false);

    setFormErrors({
      ...formErrors,
      error: '',
    });

    const getCurrentLocale = () => {
      const storedLocale = localStorage.getItem('C1C_locale');
      return storedLocale && supportedLocales.includes(storedLocale) ? storedLocale : 'en';
    };

    let accountsEndPoint;
    switch (DEPLOYMENT_TARGET) {
      case 'prod':
        accountsEndPoint = `https://accounts.cloudone.trendmicro.com/api/accounts`;
        break;
      case 'stage':
        accountsEndPoint = `https://accounts.staging-cloudone.trendmicro.com/api/accounts`;
        break;
      case 'drsim':
        accountsEndPoint = `https://accounts.drsim-cloudone.trendmicro.com/api/accounts`;
        break;
      case 'govcloud-prod':
        accountsEndPoint = `https://accounts.cloudonegov.trendmicro.com/api/accounts`;
        break;
      case 'govcloud-staging':
        accountsEndPoint = `https://accounts.staging-cloudonegov.trendmicro.com/api/accounts`;
        break;
      case 'alpha':
      case 'dev':
        accountsEndPoint =
          window.location.hostname === 'localhost'
            ? '/api/accounts'
            : `https://accounts.dev-cloudone.trendmicro.com/api/accounts`;
        break;
      default:
        accountsEndPoint = null;
        return console.error('No accounts end-point found.');
    }

    let params = {};
    if (hasMarketplaceToken) {
      params = {
        marketplace: marketplaceProvider,
        token: marketplaceToken,
      };

      accountsEndPoint = `${accountsEndPoint}?${new URLSearchParams(params).toString()}`;
    }

    const reqData = {
      alias: accountAlias,
      locale: getCurrentLocale(),
      timezone: userTimezone,
      region,
    };

    try {
      const response = await fetch(accountsEndPoint, {
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'api-version': 'v1',
          Authorization: `Bearer ${sessionStorage.getItem('idToken')}`,
        },
        body: JSON.stringify(reqData),
      });

      if (response.status === 201) {
        const { id } = await response.json();
        // Add Account ID & Fetch Services by Account
        eventDistributor.dispatch({ type: 'ADD_ACCOUNT_ID', c1AccountID: id });

        // set last accessed id
        localStorage.setItem('lastAccessedAccountID', id);

        if (marketplaceToken) {
          localStorage.removeItem('C1C_MarketplaceRequest');
          localStorage.setItem('C1C_MarketplaceSubscriptionProvider', marketplaceProvider);
        }

        // Set the flag when the account has been created through a campaign to be used in landing for sending the track event to Pendo
        if (hasCampaignReferrer) {
          localStorage.setItem('C1C_CAMPAIGN_ACCOUNT_CREATED', hasCampaignReferrer);
          localStorage.removeItem('C1C_CAMPAIGN_REFERRER');
        }

        // Add a time delay before navigating to landing page
        await waitFor(1000);
        // then navigate to services landing page
        if (shouldNotRedirectToOnboarding) {
          eventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: 'home' });
        } else {
          eventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: 'onboarding' });
        }
      } else if (response.status === 400) {
        setIsLoading(false);
        setFormErrors({
          ...formErrors,
          error: 'account_creation_limit',
        });
      } else {
        const errResp = await response.json();
        throw new Error(errResp?.message || 'Account could not be created.');
      }
    } catch (error) {
      const errMsg = !error.message ? error : error.message;
      console.error('Error: ', errMsg);
      setIsLoading(false);

      setFormErrors({
        ...formErrors,
        error: filterMessage(error),
      });
      resetForm();
    } finally {
      eventDistributor.dispatch({ type: 'TOGGLE_GLOBAL_LOADING_INDICATOR', loading: false });
    }
  };

  const defaultValues: IProps = {
    accountAlias: '',
    region: '',
    idToken: '',
  };

  const isUSRegion = selectedRegion === 'us-1';

  return (
    <>
      {isLoading ? (
        <OverlayLoader isLoading={isLoading} />
      ) : (
        <CenteredLayout>
          <FormContainer
            id="account-setup-form"
            customValidation={accountSetupValidation}
            initialValues={defaultValues}
            submitAction={(formPayload) => handleSubmit(formPayload)}
          >
            <>
              <Stack spacing="6x">
                <Text size="2xl" lineHeight="xl">
                  {t(
                    hasMarketplaceToken
                      ? `accountSetup.marketplaceTagline.${marketplaceProvider}`
                      : 'accountSetup.title'
                  )}
                </Text>
                {missingTimezone ||
                  (formErrors.error && (
                    <Stack spacing="3x">
                      {missingTimezone && (
                        <NotificationBanner
                          name="missing-timezone"
                          severity="warning"
                          variant="outline"
                          shouldOpen={true}
                          width="100%"
                          showCloseButton={true}
                          bannerButtonTxt="myProfile"
                          bannerText="accountSetup.missingTimezone"
                          clickHandler={() =>
                            eventDistributor.dispatch({
                              type: 'NAVIGATE_APP',
                              appName: 'my-user/my-profile',
                            })
                          }
                        />
                      )}
                      {formErrors.error && (
                        <NotificationBanner
                          name="account-setup-form"
                          severity="error"
                          variant="outline"
                          shouldOpen={true}
                          bannerText={
                            !formErrors.error.includes('account_creation_limit')
                              ? formErrors.error
                              : undefined
                          }
                          htmlElement={
                            formErrors.error.includes('account_creation_limit') ? (
                              <>
                                <Trans
                                  i18nKey="accountSetup.errorMessage.accountCreationLimit"
                                  components={{
                                    supportLink: <ExternalLink showIcon={false} href="/support" />,
                                  }}
                                />
                                <Box marginTop="2x">
                                  <ExternalLink
                                    showIcon={false}
                                    href={t('accountSetup.errorMessage.accountLimitationsLink')}
                                  >
                                    {t('accountSetup.errorMessage.whyAmISeeingThis')}
                                  </ExternalLink>
                                </Box>
                              </>
                            ) : undefined
                          }
                        />
                      )}
                    </Stack>
                  ))}
                {!hasMarketplaceToken && <Text>{t('accountSetup.description')}</Text>}
                <Box marginTop="1x">
                  <InputField
                    id="account-alias"
                    type="text"
                    label={t('accountAlias')}
                    name="accountAlias"
                    helperText={hasMarketplaceToken ? 'accountSetup.aliasHelperText' : ''}
                    isRequired={true}
                  />
                </Box>
                <Box>
                  <DropdownMenu
                    options={regionsList}
                    hasRegions={true}
                    label={t('region')}
                    placeholder={t('selectRegion')}
                    maxHeight={150}
                    helperText={t('accountSetup.regionHelperText')}
                    name="region"
                    isRequired={true}
                    onChange={(selectedId) => setSelectedRegion(selectedId)}
                  />
                </Box>
                {!isUSRegion && !isGovCloudRegion && (
                  <NotificationBanner
                    name="service-availability"
                    severity="warning"
                    variant="outline"
                    shouldOpen={true}
                    bannerText="accountSetup.serviceAvailability"
                    bannerLinkTxt="accountSetup.learnMore"
                    bannerLink={t('accountSetup.learnMoreLink')}
                    isLinkInline={true}
                  />
                )}
                <SubmitButton label={t('continue')} id="account-setup-submit-btn" />
              </Stack>
            </>
          </FormContainer>
        </CenteredLayout>
      )}
    </>
  );
};

AccountSetup.displayName = 'AccountSetup';
export default AccountSetup;
